/*
 * <collapsible-text> — shows a clamped preview of its content with a button to
 * expand and collapse it.
 *
 * The clamp itself is CSS, so the element only manages the open state and the
 * button. That keeps it reusable: whichever breakpoint a stylesheet chooses to
 * clamp at is the breakpoint where the control appears, and where no clamp is
 * applied the button hides itself.
 *
 * Expected markup:
 *   <collapsible-text>
 *     <div id="…" data-collapsible-content>…</div>
 *     <button type="button" data-collapsible-toggle aria-controls="…"
 *             aria-expanded="false" data-label-more="Read more"
 *             data-label-less="Show less">Read more</button>
 *   </collapsible-text>
 */
if (!customElements.get('collapsible-text')) {
  customElements.define(
    'collapsible-text',
    class CollapsibleText extends HTMLElement {
      constructor() {
        super();
        this.onToggle = this.onToggle.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.content = this.querySelector('[data-collapsible-content]');
        this.toggle = this.querySelector('[data-collapsible-toggle]');
        if (!this.content || !this.toggle) return;

        this.toggle.addEventListener('click', this.onToggle);

        this.syncToggleVisibility();
        window.addEventListener('resize', this.onResize);
      }

      disconnectedCallback() {
        this.toggle?.removeEventListener('click', this.onToggle);
        window.removeEventListener('resize', this.onResize);
      }

      get expanded() {
        return this.hasAttribute('open');
      }

      setExpanded(expanded) {
        this.toggleAttribute('open', expanded);
        this.toggle.setAttribute('aria-expanded', String(expanded));
        this.toggle.textContent = expanded ? this.toggle.dataset.labelLess : this.toggle.dataset.labelMore;
      }

      onToggle() {
        this.setExpanded(!this.expanded);
      }

      /*
       * The button is only useful when the content is actually clamped, so a
       * short description never gets a pointless control. Comparing scroll
       * height against client height detects the clamp without hard-coding
       * which breakpoint applies it.
       */
      syncToggleVisibility() {
        if (this.expanded) return;
        this.toggle.hidden = this.content.scrollHeight <= this.content.clientHeight + 1;
      }

      onResize() {
        // Collapse on resize so a card expanded on a phone does not stay open
        // at a width where the clamp no longer applies.
        if (this.expanded) this.setExpanded(false);
        this.syncToggleVisibility();
      }
    }
  );
}
